import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Trash2 } from "lucide-react";

const empty = {
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  role: "supplier",
  primary_department: "",
};

// Kept alongside the empty template so both this dialog and any parent
// filter chips (Suppliers page) render the same labels.
const ROLE_OPTIONS = [
  {
    value: "supplier",
    label: "Supplier",
    hint: "Sells stock to us — appears on the main PO destination picker.",
  },
  {
    value: "processor",
    label: "Processor",
    hint: "Cuts / laminates / finishes stock on our behalf (e.g. JV Strappings). Billed separately as toll processing.",
  },
  {
    value: "both",
    label: "Both",
    hint: "Sells AND processes for us.",
  },
];

const SupplierFormDialog = ({ open, initial, onClose, onSaved, onDelete }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/departments")
      .then(({ data }) => setDepartments(data || []))
      .catch(() => setDepartments([]));
  }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (initial?.id) {
        const { data } = await api.put(`/suppliers/${initial.id}`, form);
        const c = data?._cascade;
        if (c?.renamed && (c.stock_cards_updated || 0) > 0) {
          toast.success(
            `Renamed ${c.old_name} → ${c.new_name}`,
            {
              description: `${c.stock_cards_updated} inventory ${
                c.stock_cards_updated === 1 ? "card" : "cards"
              } retagged`,
              duration: 6000,
            },
          );
        } else {
          toast.success("Supplier updated");
        }
        onSaved?.(data);
      } else {
        const { data } = await api.post("/suppliers", form);
        toast.success("Supplier created");
        onSaved?.(data);
      }
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Deactivate "${form.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/suppliers/${initial.id}`);
      toast.success("Deactivated");
      onDelete?.(initial.id);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="supplier-form-overlay"
    >
      <div
        className="nb-card p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="supplier-form-dialog"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-eyebrow">
              {initial?.id ? "Edit supplier" : "New supplier"}
            </div>
            <h3 className="font-display text-2xl mt-1">
              {initial?.id ? form.name : "Add Supplier"}
            </h3>
          </div>
          <button onClick={onClose} className="nb-btn bg-white p-2">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <label>
            <div className="label-eyebrow mb-1">Name *</div>
            <input
              data-testid="supplier-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Role</div>
            <select
              data-testid="supplier-role-input"
              className="nb-input"
              value={form.role || "supplier"}
              onChange={(e) => set("role", e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-[#607087] mt-1">
              {ROLE_OPTIONS.find((r) => r.value === (form.role || "supplier"))?.hint}
            </div>
          </label>
          <label>
            <div className="label-eyebrow mb-1">Primary owner (department)</div>
            <select
              data-testid="supplier-primary-dept-input"
              className="nb-input"
              value={form.primary_department || ""}
              onChange={(e) => set("primary_department", e.target.value)}
            >
              <option value="">— None (visible only under "All depts") —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-[#607087] mt-1">
              When Orders / PO are filtered by dept, only suppliers whose primary owner matches will appear.
            </div>
          </label>
          <label>
            <div className="label-eyebrow mb-1">Contact name</div>
            <input
              data-testid="supplier-contact-input"
              className="nb-input"
              value={form.contact_name || ""}
              onChange={(e) => set("contact_name", e.target.value)}
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Email (PO destination)</div>
            <input
              data-testid="supplier-email-input"
              type="email"
              className="nb-input"
              value={form.email || ""}
              onChange={(e) => set("email", e.target.value)}
              placeholder="orders@supplier.com"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Phone</div>
            <input
              data-testid="supplier-phone-input"
              className="nb-input"
              value={form.phone || ""}
              onChange={(e) => set("phone", e.target.value)}
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Website (online ordering)</div>
            <input
              data-testid="supplier-website-input"
              type="url"
              className="nb-input"
              value={form.website || ""}
              onChange={(e) => set("website", e.target.value)}
              placeholder="https://shop.supplier.com"
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Address</div>
            <textarea
              data-testid="supplier-address-input"
              className="nb-input"
              rows={2}
              value={form.address || ""}
              onChange={(e) => set("address", e.target.value)}
            />
          </label>
          <label>
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="supplier-notes-input"
              className="nb-input"
              rows={2}
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
            />
          </label>
        </div>
        <div className="flex justify-between mt-5">
          {initial?.id ? (
            <button
              data-testid="supplier-delete"
              onClick={del}
              disabled={busy}
              className="nb-btn nb-btn-danger px-4 py-2.5 flex items-center gap-2"
            >
              <Trash2 size={16} strokeWidth={2.5} /> DEACTIVATE
            </button>
          ) : (
            <div />
          )}
          <button
            data-testid="supplier-save"
            onClick={save}
            disabled={busy}
            className="nb-btn nb-btn-primary px-5 py-2.5 flex items-center gap-2"
          >
            <Save size={16} strokeWidth={2.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierFormDialog;
